import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {getScoreLevelColors, scorePillChipSx} from '../../../../../utils/getScoreLevelColors';
import type {DepartmentHealthProfileSummary} from '../../types/DepartmentHealthTypes';
import {isRedeploymentPath} from '../../utils/DepartmentHealth/buildDepartmentHealthProfileDecisionGuideContext';
import {
  getDepartmentHealthProfileEffortLevel,
  getDepartmentHealthProfilePathChipStyles,
} from '../../utils/DepartmentHealth/getDepartmentHealthProfilePathChipStyles';

export interface Props {
  profile: DepartmentHealthProfileSummary;
}

export const DepartmentHealthProfileDecisionGuideMostUrgentExampleCard = ({profile}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.profileDecisionGuide.example'});

  const pathStyles = getDepartmentHealthProfilePathChipStyles(profile);
  const isRedeploy = isRedeploymentPath(profile);
  const effortLevel = getDepartmentHealthProfileEffortLevel(profile);
  const scoreColors =
    profile.targetProfileAlignmentScore !== null
      ? getScoreLevelColors(profile.targetProfileAlignmentScore)
      : null;

  const rationale = isRedeploy
    ? t('rationale.redeploy')
    : t('rationale.develop', {gapCount: profile.gapCount, effort: t(`effort.${effortLevel}`)});

  const outcome = isRedeploy ? t('outcome.redeploy') : t(`outcome.develop.${effortLevel}`);

  return (
    <Box
      sx={{
        px: 1.625,
        py: 1.375,
        borderRadius: '10px',
        bgcolor: pathStyles.bgcolor,
        ...scorePillChipSx,
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.625, flexWrap: 'wrap'}}>
        <Typography sx={{fontSize: '12px', fontWeight: 600}}>{profile.publicId}</Typography>
        {scoreColors && profile.targetProfileAlignmentScore !== null && (
          <Typography sx={{fontSize: '12px', fontWeight: 700, color: scoreColors.color}}>
            {profile.targetProfileAlignmentScore}%
          </Typography>
        )}
        <Box
          component='span'
          sx={{
            marginLeft: 'auto',
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            px: 0.875,
            py: 0.25,
            borderRadius: '5px',
            bgcolor: 'background.paper',
            color: pathStyles.color,
            ...scorePillChipSx,
          }}
        >
          {isRedeploy ? t('pathBadge.redeploy') : t('pathBadge.develop')}
        </Box>
      </Box>

      <Typography sx={{fontSize: '12px', color: 'text.secondary', lineHeight: 1.5}}>{rationale}</Typography>

      <Typography
        sx={{
          mt: 0.75,
          pt: 0.75,
          borderTop: '0.5px solid',
          borderColor: 'divider',
          fontSize: '11px',
          fontStyle: 'italic',
          color: 'text.secondary',
        }}
      >
        {outcome}
      </Typography>
    </Box>
  );
};
